// ---------------------------------------------------------------------------
// C6: Additional Puck Components (8 new)
// ---------------------------------------------------------------------------
// All use CSS variables (NO hardcoded colors). Responsive. Sensible defaults.
// ---------------------------------------------------------------------------
// No React imports needed — components are plain render functions

import { resolveVideoEmbed } from "@ease/shared/video-embed";

import {
  createStringListField,
  createStructuredArrayField,
  normalizeFaqItems,
  normalizeFeatureItems,
  normalizeGalleryItems,
  normalizePricingFeatures,
  normalizePricingTierItems,
  normalizeStatItems,
  normalizeTestimonialItems,
} from "@/components/pages/puck-authoring-fields";

// ---------------------------------------------------------------------------
// 1. Testimonials — carousel of quotes
// ---------------------------------------------------------------------------
export const Testimonials = {
  fields: {
    quotes: createStructuredArrayField({
      label: "Testimonials",
      itemLabel: "Testimonial",
      addItemLabel: "Add testimonial",
      itemFields: [
        { key: "quote", label: "Quote", type: "textarea", placeholder: "What result or transformation are you highlighting?" },
        { key: "author", label: "Customer name", type: "text", placeholder: "Customer name" },
        { key: "role", label: "Role or company", type: "text", placeholder: "Role or company" },
      ],
      getDefaultItem: () => ({ quote: "", author: "", role: "" }),
      normalizeValue: normalizeTestimonialItems,
    }),
  },
  defaultProps: {
    quotes: [
      {
        quote: "Add a short customer quote that explains the result they saw.",
        author: "Customer name",
        role: "Role or company",
      },
      {
        quote: "Use the second testimonial to reinforce trust or describe a different outcome.",
        author: "Customer name",
        role: "Role or company",
      },
    ],
  },
  render: ({ quotes }: { quotes: unknown }) => {
    const items = normalizeTestimonialItems(quotes);
    return (
      <div style={{ padding: "40px 24px", maxWidth: "800px", margin: "0 auto" }}>
        <div style={{ display: "flex", gap: "24px", overflowX: "auto", paddingBottom: "8px" }}>
          {items.map((item, i) => (
            <div key={i} style={{ minWidth: "300px", padding: "24px", borderRadius: "12px", border: "1px solid var(--color-border)", background: "var(--color-card)" }}>
              <p style={{ fontSize: "0.95rem", lineHeight: 1.6, color: "var(--color-foreground)", fontStyle: "italic" }}>"{item.quote}"</p>
              <div style={{ marginTop: "16px" }}>
                <p style={{ fontWeight: 600, color: "var(--color-foreground)" }}>{item.author}</p>
                <p style={{ fontSize: "0.85rem", color: "var(--color-muted-foreground)" }}>{item.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 2. Pricing Table — 3 tiers
// ---------------------------------------------------------------------------
export const PricingTable = {
  fields: {
    tiers: createStructuredArrayField({
      label: "Pricing tiers",
      itemLabel: "Tier card",
      addItemLabel: "Add tier",
      maxItems: 6,
      itemFields: [
        { key: "name", label: "Plan name", type: "text", placeholder: "Growth" },
        { key: "price", label: "Price", type: "text", placeholder: "EUR89/mo" },
        {
          key: "features",
          label: "Features",
          type: "string-list",
          placeholder: "One feature per item",
        },
        { key: "ctaText", label: "CTA label", type: "text", placeholder: "Choose Growth" },
        {
          key: "isPopular",
          label: "Popularity badge",
          type: "select",
          options: [
            { label: "Standard", value: "false" },
            { label: "Most popular", value: "true" },
          ],
        },
      ],
      getDefaultItem: () => ({
        name: "",
        price: "",
        features: [""],
        ctaText: "",
        isPopular: "false",
      }),
      normalizeValue: normalizePricingTierItems,
    }),
  },
  defaultProps: {
    tiers: [
      {
        name: "Starter",
        price: "EUR29/mo",
        features: ["Up to 500 contacts", "1 team member", "Email support"],
        ctaText: "Choose Starter",
        isPopular: false,
      },
      {
        name: "Growth",
        price: "EUR89/mo",
        features: ["Up to 5,000 contacts", "5 team members", "Priority support", "Automation tools"],
        ctaText: "Choose Growth",
        isPopular: true,
      },
      {
        name: "Premium",
        price: "Contact sales",
        features: ["Unlimited contacts", "Unlimited team members", "Priority onboarding", "Advanced integrations"],
        ctaText: "Choose Premium",
        isPopular: false,
      },
    ],
  },
  render: ({ tiers }: { tiers: unknown }) => {
    const normalizedTiers = normalizePricingTierItems(tiers).map((tier) => ({
      ...tier,
      highlight: tier.isPopular === true,
    }));
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(normalizedTiers.length, 1)}, 1fr)`, gap: "16px", padding: "40px 24px", maxWidth: "900px", margin: "0 auto" }}>
        {normalizedTiers.map((tier, i) => (
          <div key={i} style={{ padding: "24px", borderRadius: "12px", border: tier.highlight ? "2px solid var(--color-primary)" : "1px solid var(--color-border)", background: "var(--color-card)", transform: tier.highlight ? "scale(1.02)" : "none", boxShadow: tier.highlight ? "var(--shadow-lg)" : "var(--shadow-sm)" }}>
            {tier.highlight && <div style={{ background: "var(--color-primary)", color: "white", textAlign: "center", padding: "4px", borderRadius: "4px", fontSize: "0.75rem", fontWeight: 600, marginBottom: "12px" }}>Most Popular</div>}
            <h3 style={{ fontSize: "1.25rem", fontWeight: 600 }}>{String(tier.name ?? "")}</h3>
            <p style={{ fontSize: "2rem", fontWeight: 700, color: "var(--color-primary)", margin: "8px 0" }}>{String(tier.price ?? "")}</p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {normalizePricingFeatures(tier.features).map((f, j) => (
                <li key={j} style={{ padding: "6px 0", fontSize: "0.875rem", color: "var(--color-muted-foreground)", borderBottom: "1px solid var(--color-border)" }}>✓ {f}</li>
              ))}
            </ul>
            <button style={{ width: "100%", marginTop: "16px", padding: "10px", background: tier.highlight ? "var(--color-primary)" : "var(--color-muted)", color: tier.highlight ? "white" : "var(--color-foreground)", border: "none", borderRadius: "8px", fontWeight: 600, cursor: "pointer" }}>
              {String(tier.ctaText ?? `Choose ${String(tier.name ?? "plan")}`)}
            </button>
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 3. FAQ Accordion
// ---------------------------------------------------------------------------
export const FaqAccordion = {
  fields: {
    items: createStructuredArrayField({
      label: "FAQ items",
      itemLabel: "Question",
      addItemLabel: "Add question",
      itemFields: [
        { key: "question", label: "Question", type: "text", placeholder: "What do customers ask before buying?" },
        { key: "answer", label: "Answer", type: "textarea", placeholder: "Write a direct answer in plain language." },
      ],
      getDefaultItem: () => ({ question: "", answer: "" }),
      normalizeValue: normalizeFaqItems,
    }),
  },
  defaultProps: {
    items: [
      { question: "How long does setup take?", answer: "Add your brand, update the copy, and publish when you are ready." },
      { question: "Can I update this later?", answer: "Yes. You can edit copy, pricing, images, and sections any time." },
      { question: "Do I need a developer?", answer: "No. This page is meant to be managed by non-technical teammates." },
    ],
  },
  render: ({ items }: { items: unknown }) => {
    const faqs = normalizeFaqItems(items);
    return (
      <div style={{ maxWidth: "700px", margin: "0 auto", padding: "40px 24px" }}>
        {faqs.map((faq, i) => (
          <details key={i} style={{ borderBottom: "1px solid var(--color-border)", padding: "16px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600, color: "var(--color-foreground)" }}>{faq.question}</summary>
            <p style={{ marginTop: "8px", color: "var(--color-muted-foreground)", lineHeight: 1.6 }}>{faq.answer}</p>
          </details>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 4. Video Embed
// ---------------------------------------------------------------------------
export const VideoEmbed = {
  fields: { url: { type: "text", label: "YouTube or Vimeo URL" }, caption: { type: "text" } },
  defaultProps: { url: "", caption: "Optional caption" },
  render: ({ url, caption }: { url: string; caption: string }) => {
    const embed = resolveVideoEmbed(url);
    return (
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "24px" }}>
        {embed ? (
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: "12px" }}>
            <iframe
              src={embed.embedUrl}
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
              allowFullScreen
              referrerPolicy="no-referrer"
              loading="lazy"
              sandbox={embed.isSandboxed ? "allow-scripts allow-same-origin allow-presentation" : undefined}
            />
          </div>
        ) : (
          <div style={{ aspectRatio: "16/9", background: "var(--color-muted)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-muted-foreground)" }}>
            Video embed unavailable. Use a supported public URL or a Vimeo URL with its private hash.
          </div>
        )}
        {caption && <p style={{ textAlign: "center", marginTop: "8px", fontSize: "0.875rem", color: "var(--color-muted-foreground)" }}>{caption}</p>}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 5. Gallery — image grid
// ---------------------------------------------------------------------------
export const Gallery = {
  fields: {
    images: createStructuredArrayField({
      label: "Gallery images",
      itemLabel: "Image",
      addItemLabel: "Add image",
      itemFields: [
        { key: "src", label: "Image", type: "image", placeholder: "Choose or upload an image" },
        { key: "alt", label: "Alt text", type: "text", placeholder: "Describe the image for accessibility" },
        { key: "caption", label: "Caption", type: "text", placeholder: "Optional caption" },
      ],
      getDefaultItem: () => ({ src: "", alt: "", caption: "" }),
      normalizeValue: normalizeGalleryItems,
    }),
    columns: { type: "select", label: "Columns", options: [{ label: "2", value: "2" }, { label: "3", value: "3" }, { label: "4", value: "4" }] },
  },
  defaultProps: {
    images: [
      { src: "", alt: "Add your first gallery image", caption: "Upload real photos from your business" },
      { src: "", alt: "Add your second gallery image", caption: "Use captions only when they add context" },
      { src: "", alt: "Add your third gallery image", caption: "" },
    ],
    columns: "3",
  },
  render: ({ images, columns }: { images: unknown; columns: string }) => {
    const items = normalizeGalleryItems(images);
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: "8px", padding: "24px" }}>
        {items.some((item) => item.src) ? items.map((item, i) => (
          <div key={i} style={{ aspectRatio: "1", overflow: "hidden", borderRadius: "8px" }}>
            {item.src ? (
              <img src={item.src} alt={item.alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", background: "var(--color-muted)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-muted-foreground)", fontSize: "0.75rem", textAlign: "center", padding: "12px" }}>
                Upload an image
              </div>
            )}
          </div>
        )) : (
          Array.from({ length: parseInt(columns) * 2 }, (_, i) => (
            <div key={i} style={{ aspectRatio: "1", background: "var(--color-muted)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-muted-foreground)", fontSize: "0.75rem" }}>
              Image {i + 1}
            </div>
          ))
        )}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 6. Feature Grid — icon + title + description
// ---------------------------------------------------------------------------
export const FeatureGrid = {
  fields: {
    features: createStructuredArrayField({
      label: "Feature cards",
      itemLabel: "Feature",
      addItemLabel: "Add feature",
      itemFields: [
        { key: "title", label: "Title", type: "text", placeholder: "Feature title" },
        { key: "description", label: "Description", type: "textarea", placeholder: "Explain why this matters to the customer." },
      ],
      getDefaultItem: () => ({ title: "", description: "" }),
      normalizeValue: normalizeFeatureItems,
    }),
    columns: { type: "select", label: "Columns", options: [{ label: "2", value: "2" }, { label: "3", value: "3" }] },
  },
  defaultProps: {
    features: [
      { title: "Fast setup", description: "Give editors a clear next step instead of making them configure everything from scratch." },
      { title: "Built-in trust", description: "Highlight guarantees, proof points, or service details customers care about before they buy." },
      { title: "Easy updates", description: "Swap copy, pricing, and images without touching code or formatting rules." },
    ],
    columns: "3",
  },
  render: ({ features, columns }: { features: unknown; columns: string }) => {
    const items = normalizeFeatureItems(features);
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: "24px", padding: "40px 24px", maxWidth: "900px", margin: "0 auto" }}>
        {items.map((item, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "var(--color-primary-light)", margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-primary)", fontSize: "1.25rem" }}>✦</div>
            <h4 style={{ fontWeight: 600, marginBottom: "4px" }}>{item.title}</h4>
            <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", lineHeight: 1.5 }}>{item.description}</p>
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 7. Stats Counter — number + label
// ---------------------------------------------------------------------------
export const StatsCounter = {
  fields: {
    stats: createStructuredArrayField({
      label: "Stats",
      itemLabel: "Stat",
      addItemLabel: "Add stat",
      itemFields: [
        { key: "number", label: "Number", type: "text", placeholder: "e.g. 24/7 or 98%" },
        { key: "label", label: "Label", type: "text", placeholder: "What does this number represent?" },
      ],
      getDefaultItem: () => ({ number: "", label: "" }),
      normalizeValue: normalizeStatItems,
    }),
  },
  defaultProps: {
    stats: [
      { number: "24/7", label: "Support availability" },
      { number: "48h", label: "Typical launch timeline" },
      { number: "3", label: "Simple steps to publish" },
    ],
  },
  render: ({ stats }: { stats: unknown }) => {
    const items = normalizeStatItems(stats);
    return (
      <div style={{ display: "flex", justifyContent: "center", gap: "48px", padding: "40px 24px", flexWrap: "wrap" }}>
        {items.map((item, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2.5rem", fontWeight: 700, color: "var(--color-primary)", fontVariantNumeric: "tabular-nums" }}>{item.number}</div>
            <div style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", marginTop: "4px" }}>{item.label}</div>
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 8. Contact Form — name + email + message
// ---------------------------------------------------------------------------
export const ContactForm = {
  fields: {
    heading: { type: "text" },
    submitLabel: { type: "text" },
    successMessage: { type: "text" },
  },
  defaultProps: { heading: "Get in Touch", submitLabel: "Send Message", successMessage: "Thanks! We'll be in touch soon." },
  render: ({ heading, submitLabel }: { heading: string; submitLabel: string; successMessage: string }) => (
    <div style={{ maxWidth: "500px", margin: "0 auto", padding: "40px 24px" }}>
      <h3 style={{ fontSize: "1.5rem", fontWeight: 600, textAlign: "center", marginBottom: "24px" }}>{heading}</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <input type="text" placeholder="Your name" style={{ width: "100%", padding: "10px 16px", border: "1px solid var(--color-border)", borderRadius: "8px", boxSizing: "border-box", background: "var(--color-background)", color: "var(--color-foreground)" }} />
        <input type="email" placeholder="Your email" style={{ width: "100%", padding: "10px 16px", border: "1px solid var(--color-border)", borderRadius: "8px", boxSizing: "border-box", background: "var(--color-background)", color: "var(--color-foreground)" }} />
        <textarea placeholder="Your message" rows={4} style={{ width: "100%", padding: "10px 16px", border: "1px solid var(--color-border)", borderRadius: "8px", boxSizing: "border-box", resize: "none", background: "var(--color-background)", color: "var(--color-foreground)" }} />
        <button style={{ width: "100%", padding: "12px", background: "var(--color-primary)", color: "white", border: "none", borderRadius: "8px", fontWeight: 600, cursor: "pointer" }}>
          {submitLabel}
        </button>
      </div>
    </div>
  ),
};
